/**
 * Due date filter presets for tasks-datatable.
 *
 * Implements DatePresetConfig interface for due date filtering.
 * Provides preset IDs, detection logic, and date value application.
 *
 * These are the QUICK FILTER CHIP presets shown in the filter panel.
 * Naming follows slaResolutionPresets pattern for consistency.
 *
 * Note: "Due This Week" is not a preset - it uses explicit date ranges
 * computed by metricsConfig.ts (due_date_after=today, due_date_before=friday).
 */

import { getToday, getYesterday } from '@/utils/dateUtils';

import type {
  DateFilterState,
  DatePresetConfig,
  PresetDateValues,
} from '../../common/DataTable/filterPresets/types';

// ============================================================================
// PRESET IDS - Quick filter chip values (matches slaResolutionPresets pattern)
// ============================================================================

export const DUE_DATE_PRESET_IDS = {
  BEFORE_TODAY: 'before_today',
  TODAY: 'today',
  NONE: 'none',
} as const;

export type DueDatePresetId =
  (typeof DUE_DATE_PRESET_IDS)[keyof typeof DUE_DATE_PRESET_IDS];

// ============================================================================
// PRESET DETECTION - Determine which preset matches current filter state
// ============================================================================

/**
 * Determine which preset (if any) is currently active based on filter state.
 * Returns array for compatibility with chip selection APIs.
 */
export function getActiveDueDatePreset(
  state: DateFilterState,
): DueDatePresetId[] {
  const { startDate, endDate, noDueDate } = state;

  if (noDueDate) {
    return [DUE_DATE_PRESET_IDS.NONE];
  }

  const today = getToday();
  const yesterday = getYesterday();

  // "Today" = both start and end are today
  if (startDate === today && endDate === today) {
    return [DUE_DATE_PRESET_IDS.TODAY];
  }

  // "Before Today" = due before today (end is yesterday, inclusive)
  if (!startDate && endDate === yesterday) {
    return [DUE_DATE_PRESET_IDS.BEFORE_TODAY];
  }

  return [];
}

// ============================================================================
// PRESET APPLICATION - Compute date values for a given preset
// ============================================================================

/**
 * Get the date values that should be applied for a given preset.
 * Returns undefined values to indicate "should be cleared".
 */
export function getPresetDateValues(presetId: string): PresetDateValues {
  const today = getToday();
  const yesterday = getYesterday();

  switch (presetId) {
    case DUE_DATE_PRESET_IDS.BEFORE_TODAY:
      return { startDate: undefined, endDate: yesterday, noDueDate: false };

    case DUE_DATE_PRESET_IDS.TODAY:
      return { startDate: today, endDate: today, noDueDate: false };

    case DUE_DATE_PRESET_IDS.NONE:
      return { startDate: undefined, endDate: undefined, noDueDate: true };

    default:
      // Unknown preset - clear everything
      return { startDate: undefined, endDate: undefined, noDueDate: false };
  }
}

// ============================================================================
// EXPORT CONFIG OBJECT (implements DatePresetConfig interface)
// ============================================================================

export const dueDatePresetConfig: DatePresetConfig<DueDatePresetId> = {
  IDS: DUE_DATE_PRESET_IDS,
  getActivePreset: getActiveDueDatePreset,
  getPresetValues: getPresetDateValues,
};
